import React from 'react';

import { APIConfig } from 'src/config';
import {
  TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL,
} from 'src/util/constants';
import { convertEnergyPricePowertracerToPartner } from 'src/util/conversions';
import { unitOfMeasure } from 'src/util/i18n/energyPrice';

/**
 * Formats the feedback shown when a community price is invalid. When both configured
 * community price bounds for the direction are available, the feedback states the
 * accepted range in the locale's energy price unit so the user can correct their input.
 * @param {import('react-intl').IntlShape} intl - The intl object for internationalization
 * @param {import('react-intl').IntlShape} intlCurrency - Currency-aware intl configuration
 *   used to convert the Powertracer-format ($/Wh) bounds to the Partner format and to label
 *   them with the Partner unit.
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL} tradeDirection - The trade direction
 * @returns {React.ReactNode[] | string} Localized invalid price feedback
 */
export const formatPriceInvalidFeedback = (intl, intlCurrency, tradeDirection) => {
  const {
    COMMUNITY_BUY_PRICE_MAX, COMMUNITY_BUY_PRICE_MIN,
    COMMUNITY_SELL_PRICE_MAX, COMMUNITY_SELL_PRICE_MIN,
  } = APIConfig();
  const [minimum, maximum] = tradeDirection === TRADE_DIRECTION_BUY
    ? [COMMUNITY_BUY_PRICE_MIN, COMMUNITY_BUY_PRICE_MAX]
    : [COMMUNITY_SELL_PRICE_MIN, COMMUNITY_SELL_PRICE_MAX];

  if (minimum === undefined || maximum === undefined) {
    return intl.formatMessage({
      id: 'trade_rule.trade_rule_set_community.i18n.helpers.invalid_price.feedback',
      defaultMessage: 'Invalid price',
    });
  }

  return intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.i18n.helpers.price_out_of_range.feedback',
    defaultMessage: 'Enter a price from {minimum, number, :: .000#} to {maximum, number, :: .000#} {energyPriceUnit}',
  }, {
    minimum: convertEnergyPricePowertracerToPartner(intlCurrency, minimum).toNumber(),
    maximum: convertEnergyPricePowertracerToPartner(intlCurrency, maximum).toNumber(),
    // The bounds are converted with the partner locale, so label them with its unit too.
    energyPriceUnit: unitOfMeasure(intlCurrency),
  });
};

/**
 * Returns the appropriate price label based on trade direction.
 * @param {import('react-intl').IntlShape} intl - The intl object for internationalization
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL | string} tradeDirection -
 * The trade direction (buy/sell)
 * @returns {string} Localized price label for the given direction or empty string for
 * invalid inputs
 */
export const getPriceLabel = (intl, tradeDirection) => {
  if (!intl) return '';

  switch (tradeDirection) {
    case TRADE_DIRECTION_BUY:
      return intl.formatMessage({
        id: 'trade_rule.trade_rule_set_community.i18n.helpers.maximum_buy_price.label',
        defaultMessage: 'Maximum buy price',
      });
    case TRADE_DIRECTION_SELL:
      return intl.formatMessage({
        id: 'trade_rule.trade_rule_set_community.i18n.helpers.minimum_sell_price.label',
        defaultMessage: 'Minimum sell price',
      });
    default:
      return '';
  }
};

/**
 * Returns the appropriate price help text based on trade direction.
 * @param {import('react-intl').IntlShape} intl - The request UI formatter.
 * @param {import('react-intl').IntlShape} intlCurrency - Partner currency formatter.
 * @param {string} tradeDirection - The trade direction (buy/sell)
 * @returns {React.ReactNode[] | string} Localized price help text for the given direction
 */
export const getPriceHelpText = (intl, intlCurrency, tradeDirection) => {
  if (!intl || !intlCurrency) return '';

  const energyPriceUnit = unitOfMeasure(intlCurrency);

  switch (tradeDirection) {
    case TRADE_DIRECTION_BUY:
      return intl.formatMessage({
        id: 'trade_rule.trade_rule_set_community.i18n.helpers.maximum_buy_price.hint',
        defaultMessage: 'What is the maximum price, in {energyPriceUnit}, that you will buy your energy from the community?',
      }, { energyPriceUnit });
    case TRADE_DIRECTION_SELL:
      return intl.formatMessage({
        id: 'trade_rule.trade_rule_set_community.i18n.helpers.minimum_sell_price.hint',
        defaultMessage: 'What is the minimum price, in {energyPriceUnit}, that you will sell your energy to the community?',
      }, { energyPriceUnit });
    default:
      return '';
  }
};
